/**
 * useVimsottariDasha Hook
 * Manages Vimsottari Dasha data fetching and sub-period calculations
 *
 * Features:
 * - Lazy loading: API called only when loadData() is invoked
 * - Response caching: stores in memory to avoid duplicate requests
 * - Configurable starting point (Chandra, Lagna, etc.)
 * - Configurable calculation method (year type)
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { JatakaResponse } from '../../../../../models/api';
import { jatakaApiService } from '../../../../../services/JatakaApiService';
import {
  DashaPeriod,
  VimsottariDasaResponse,
  MAX_DASHA_LEVEL,
  DashaStartingPoint,
  DashaYearType,
  DashaCustomStartPoint,
  DEFAULT_STARTING_POINT,
  DEFAULT_YEAR_TYPE
} from '../data/VimsottariDashaData';
import {
  mahadasaToDashaPeriod,
  markCurrentPeriods,
  getCurrentPath,
  DashaCacheManager
} from '../calculators/VimsottariCalculator';

// In-memory cache for API responses (keyed by datetime_iso + startingPoint + yearType)
const responseCache = new Map<string, VimsottariDasaResponse>();

export interface UseVimsottariDashaOptions {
  /** Auto-expand to current period on load */
  autoExpandToCurrent?: boolean;
  /** Target date for current period (default: now) */
  targetDate?: Date;
}

export interface UseVimsottariDashaReturn {
  /** API response data */
  response: VimsottariDasaResponse | null;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** Data has been loaded */
  isLoaded: boolean;
  /** Mahadasha periods converted to DashaPeriod */
  mahadasas: DashaPeriod[];
  /** Currently visible periods (based on expanded path) */
  visiblePeriods: DashaPeriod[];
  /** Ancestor periods (expanded parents with full data) */
  ancestorPeriods: DashaPeriod[];
  /** Expanded path: planet names at each level */
  expandedPath: string[];
  /** Expand a period (show its sub-periods) */
  expand: (planet: string, level: number) => void;
  /** Collapse to a specific level */
  collapse: (level: number) => void;
  /** Go to current period (expand all levels to current) */
  goToCurrent: () => void;
  /** Check if a period is expanded */
  isExpanded: (planets: string[]) => boolean;
  /** Current level depth (1-6) */
  currentLevel: number;
  /** Can expand deeper? */
  canExpandDeeper: boolean;
  /** Load data from API (call when section expands) */
  loadData: () => Promise<void>;
  /** Force refresh data from API (ignore cache) */
  refresh: () => Promise<void>;
  /** Current starting point */
  startingPoint: DashaStartingPoint;
  /** Set starting point (triggers reload) */
  setStartingPoint: (point: DashaStartingPoint) => void;
  /** Current year type */
  yearType: DashaYearType;
  /** Set year type (triggers reload) */
  setYearType: (type: DashaYearType) => void;
  /** Custom year days (for user_defined type) */
  customYearDays: number | null;
  /** Set custom year days */
  setCustomYearDays: (days: number | null) => void;
  /** Custom starting point (for Custom option) */
  customStartPoint: DashaCustomStartPoint;
  /** Set custom starting point */
  setCustomStartPoint: (custom: DashaCustomStartPoint) => void;
  /** Set custom year days and switch to user_defined */
  setCustomYearDaysAndFetch: (days: number) => void;
}

/**
 * Get longitude for a starting point from API response
 */
function getReferenceLongitude(
  apiResponse: JatakaResponse,
  startingPoint: DashaStartingPoint
): number {
  const planets = apiResponse.graha_positions?.planets || [];

  // Map starting point to planet name
  const planetMap: Record<string, string> = {
    Chandra: 'Chandra',
    Lagna: 'Lagna',
    Surya: 'Surya',
    Mangala: 'Mangala',
    Budha: 'Budha',
    Guru: 'Guru',
    Shukra: 'Shukra',
    Shani: 'Shani',
    Rahu: 'Rahu',
    Ketu: 'Ketu',
  };

  const planetName = planetMap[startingPoint];
  const planet = planets.find(
    p => p.name === planetName || p.short_name === planetName.substring(0, 2)
  );

  if (planet?.position) {
    // Parse longitude from position string "rasi|degrees|longitude|speed"
    const parts = planet.position.split('|');
    if (parts.length >= 3) {
      return parseFloat(parts[2]) || 0;
    }
  }

  // Fallback to 0 if not found
  return 0;
}

/**
 * Hook for Vimsottari Dasha management
 */
export function useVimsottariDasha(
  apiResponse: JatakaResponse | null | undefined,
  options: UseVimsottariDashaOptions = {}
): UseVimsottariDashaReturn {
  const { autoExpandToCurrent = false, targetDate = new Date() } = options;

  // State
  const [response, setResponse] = useState<VimsottariDasaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedPath, setExpandedPath] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Starting point and year type state
  const [startingPoint, setStartingPointState] = useState<DashaStartingPoint>(DEFAULT_STARTING_POINT);
  const [yearType, setYearTypeState] = useState<DashaYearType>(DEFAULT_YEAR_TYPE);
  const [customYearDays, setCustomYearDays] = useState<number | null>(null);
  const [customStartPoint, setCustomStartPointState] = useState<DashaCustomStartPoint>({ degrees: 0, label: 'Cu' });

  // Cache manager for sub-periods (preserved across renders)
  const cacheRef = useRef(new DashaCacheManager());

  // Convert API Mahadasas to DashaPeriod array
  const mahadasas = useMemo<DashaPeriod[]>(() => {
    if (!response?.mahadasa_periods) return [];
    return markCurrentPeriods(
      response.mahadasa_periods.map(mahadasaToDashaPeriod),
      targetDate
    );
  }, [response, targetDate]);

  // Calculate visible periods and ancestor periods based on expanded path
  const { visiblePeriods, ancestorPeriods } = useMemo<{ visiblePeriods: DashaPeriod[]; ancestorPeriods: DashaPeriod[] }>(() => {
    if (expandedPath.length === 0) {
      return { visiblePeriods: mahadasas, ancestorPeriods: [] };
    }

    const ancestors: DashaPeriod[] = [];

    // Find the Mahadasha that matches first planet in path
    const mahadasha = mahadasas.find(
      m => m.planets[0] === expandedPath[0]
    );

    if (!mahadasha) return { visiblePeriods: mahadasas, ancestorPeriods: [] };

    let parentPeriod: DashaPeriod = mahadasha;
    ancestors.push(parentPeriod);

    // Walk down the path, calculating each level
    for (let i = 1; i < expandedPath.length; i++) {
      const subPeriods = cacheRef.current.getSubPeriods(parentPeriod);
      const nextPeriod = subPeriods.find(
        p => p.planets[p.planets.length - 1] === expandedPath[i]
      );

      if (!nextPeriod) break;
      parentPeriod = nextPeriod;
      ancestors.push(parentPeriod);
    }

    // Return sub-periods of the deepest expanded period
    const children = cacheRef.current.getSubPeriods(parentPeriod);
    return {
      visiblePeriods: markCurrentPeriods(children, targetDate),
      ancestorPeriods: ancestors
    };
  }, [mahadasas, expandedPath, targetDate]);

  // Current level
  const currentLevel = expandedPath.length + 1;
  const canExpandDeeper = currentLevel < MAX_DASHA_LEVEL;

  // Fetch data from API with explicit params (linear flow, no state dependencies)
  const fetchWithParams = useCallback(async (
    point: DashaStartingPoint,
    year: DashaYearType,
    customDays: number | null,
    customPoint: DashaCustomStartPoint,
    forceRefresh = false
  ) => {
    if (!apiResponse?.basic_info || !apiResponse?.graha_positions) {
      return;
    }

    const basicInfo = apiResponse.basic_info;

    // Build cache key with explicit params (include custom degrees for Custom point)
    const pointKey = point === 'Custom' ? `Custom_${customPoint.degrees}` : point;
    const key = `${basicInfo.datetime_iso}_${pointKey}_${year}_${customDays || ''}`;

    // Check cache first (unless force refresh)
    if (!forceRefresh && responseCache.has(key)) {
      const cached = responseCache.get(key)!;
      setResponse(cached);
      setIsLoaded(true);
      setError(null);

      if (autoExpandToCurrent) {
        const currentPath = getCurrentPath(cached.mahadasa_periods, targetDate);
        if (currentPath.length > 0) {
          setExpandedPath(currentPath.slice(0, -1));
        }
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // For Custom, use custom degrees; otherwise get from API response
      const referenceLongitude = point === 'Custom'
        ? customPoint.degrees
        : getReferenceLongitude(apiResponse, point);
      const referenceName = point === 'Custom' ? customPoint.label : point;

      let timezoneOffset = (apiResponse.calculation_metadata as any)?.timezone_offset;
      if (timezoneOffset === undefined) {
        const timezone = basicInfo.timezone || '';
        if (timezone === 'UTC' || timezone.startsWith('0:00:00')) {
          timezoneOffset = 0;
        } else {
          const utcMatch = timezone.match(/^UTC([+-])(\d{2}):(\d{2})$/);
          if (utcMatch) {
            const sign = utcMatch[1] === '+' ? 1 : -1;
            const hours = parseInt(utcMatch[2], 10);
            const minutes = parseInt(utcMatch[3], 10);
            timezoneOffset = sign * (hours + minutes / 60);
          } else {
            const timezoneMatch = timezone.match(/^(-?\d+):(\d+):(\d+)/);
            if (timezoneMatch) {
              const hours = parseInt(timezoneMatch[1], 10);
              const minutes = parseInt(timezoneMatch[2], 10);
              timezoneOffset = hours + minutes / 60;
            } else {
              timezoneOffset = 0;
            }
          }
        }
      }

      const result = await jatakaApiService.calculateVimsottariDasha({
        datetime: basicInfo.datetime_iso,
        latitude: basicInfo.latitudeValue,
        longitude: basicInfo.longitudeValue,
        reference_longitude: referenceLongitude,
        reference_name: referenceName,
        timezone_offset: timezoneOffset,
        place_name: basicInfo.place_name || undefined,
        altitude: parseFloat(basicInfo.altitude) || 0,
        year_type: year,
        user_year_days: year === 'user_defined' ? (customDays || undefined) : undefined
      });

      if (result.success && result.data) {
        responseCache.set(key, result.data);
        setResponse(result.data);
        setIsLoaded(true);
        cacheRef.current.clear();

        if (autoExpandToCurrent) {
          const currentPath = getCurrentPath(result.data.mahadasa_periods, targetDate);
          if (currentPath.length > 0) {
            setExpandedPath(currentPath.slice(0, -1));
          }
        }
      } else {
        setError(result.error?.message || 'Failed to calculate Vimsottari Dasha');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [apiResponse, autoExpandToCurrent, targetDate]);

  // Public: Load data (lazy, uses cache)
  const loadData = useCallback(async () => {
    await fetchWithParams(startingPoint, yearType, customYearDays, customStartPoint, false);
  }, [fetchWithParams, startingPoint, yearType, customYearDays, customStartPoint]);

  // Public: Force refresh (ignores cache)
  const refresh = useCallback(async () => {
    await fetchWithParams(startingPoint, yearType, customYearDays, customStartPoint, true);
  }, [fetchWithParams, startingPoint, yearType, customYearDays, customStartPoint]);

  // Set starting point - LINEAR: update state, clear UI, fetch with new value
  const setStartingPoint = useCallback((point: DashaStartingPoint) => {
    setStartingPointState(point);
    setExpandedPath([]);
    cacheRef.current.clear();
    // Fetch immediately with new point value (not from state)
    fetchWithParams(point, yearType, customYearDays, customStartPoint, false);
  }, [fetchWithParams, yearType, customYearDays, customStartPoint]);

  // Set year type - LINEAR: update state, clear UI, fetch with new value
  const setYearType = useCallback((type: DashaYearType) => {
    setYearTypeState(type);
    setExpandedPath([]);
    cacheRef.current.clear();
    // Fetch immediately with new year type value (not from state)
    fetchWithParams(startingPoint, type, customYearDays, customStartPoint, false);
  }, [fetchWithParams, startingPoint, customYearDays, customStartPoint]);

  // Set custom starting point - for Custom option
  const setCustomStartPoint = useCallback((custom: DashaCustomStartPoint) => {
    setCustomStartPointState(custom);
    setStartingPointState('Custom');
    setExpandedPath([]);
    cacheRef.current.clear();
    // Fetch immediately with custom values
    fetchWithParams('Custom', yearType, customYearDays, custom, false);
  }, [fetchWithParams, yearType, customYearDays]);

  // Set custom year days - for user_defined year type
  const setCustomYearDaysAndFetch = useCallback((days: number) => {
    setCustomYearDays(days);
    setYearTypeState('user_defined');
    setExpandedPath([]);
    cacheRef.current.clear();
    // Fetch immediately with user_defined and custom days
    fetchWithParams(startingPoint, 'user_defined', days, customStartPoint, false);
  }, [fetchWithParams, startingPoint, customStartPoint]);

  // Expand a period
  const expand = useCallback((planet: string, level: number) => {
    setExpandedPath(prev => {
      // Replace path from this level onwards
      const newPath = prev.slice(0, level - 1);
      newPath.push(planet);
      return newPath;
    });
  }, []);

  // Collapse to level
  const collapse = useCallback((level: number) => {
    setExpandedPath(prev => prev.slice(0, level - 1));
  }, []);

  // Go to current period
  const goToCurrent = useCallback(() => {
    if (!response?.mahadasa_periods) return;

    const currentPath = getCurrentPath(response.mahadasa_periods, targetDate);
    if (currentPath.length > 0) {
      // Expand to show current period (one level before deepest)
      setExpandedPath(currentPath.slice(0, -1));
    }
  }, [response, targetDate]);

  // Check if period is expanded
  const isExpanded = useCallback((planets: string[]): boolean => {
    if (planets.length === 0) return false;
    if (planets.length > expandedPath.length) return false;

    // Check if all planets in the path match
    for (let i = 0; i < planets.length; i++) {
      if (planets[i] !== expandedPath[i]) return false;
    }
    return true;
  }, [expandedPath]);

  return {
    response,
    loading,
    error,
    isLoaded,
    mahadasas,
    visiblePeriods,
    ancestorPeriods,
    expandedPath,
    expand,
    collapse,
    goToCurrent,
    isExpanded,
    currentLevel,
    canExpandDeeper,
    loadData,
    refresh,
    startingPoint,
    setStartingPoint,
    yearType,
    setYearType,
    customYearDays,
    setCustomYearDays,
    customStartPoint,
    setCustomStartPoint,
    setCustomYearDaysAndFetch
  };
}

export default useVimsottariDasha;
